import { useState } from 'react';
import {
  Button,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
} from '@vnetwork/ui/primitives';
import { MoreHorizontal } from 'lucide-react';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useApproveInvoice,
  useCancelInvoice,
  useDeleteInvoice,
  useRollbackInvoice,
  useAddInvoiceUpdate,
  type InvoiceDetail,
  type InvoiceStatus,
} from '@/api/invoices';
import { InvoiceSendMailDrawer } from './invoice-send-mail-drawer';
import { InvoicePreviewMailDialog } from './invoice-preview-mail-dialog';
import { InvoiceMarkPaidDrawer } from './invoice-mark-paid-drawer';

/** Cancellation / deletion reason categories (from the legacy CancelOrDelete modal). */
const REASON_CATEGORIES = [
  { value: 'because-error-on-billing', label: 'Billing error' },
  { value: 'because-customer-cancel-service', label: 'Customer cancelled service' },
  { value: 'because-not-contact-customer', label: 'Could not reach customer' },
  { value: 'because-other-reasons', label: 'Other reason' },
] as const;

/** Statuses from which an invoice can still be cancelled. */
const CANCELLABLE: InvoiceStatus[] = ['waiting-confirm', 'approved'];
/** Statuses that a paid invoice can be rolled back from. */
const ROLLBACKABLE: InvoiceStatus[] = ['paid', 'done'];

type DialogKind = 'approve' | 'cancel' | 'delete' | 'rollback' | 'addUpdate' | null;

/**
 * Status-gated invoice lifecycle actions. Primary buttons: Approve (waiting),
 * Mark paid (approved). A ⋯ menu holds the rest — Send / Preview mail, Add update,
 * Cancel, Rollback (paid/done), Delete (waiting). All wired to their mutations;
 * the USER runs every live create/edit/delete — the assistant only builds + wires.
 */
export function InvoiceActions({ id, detail }: { id: number; detail: InvoiceDetail }) {
  const toast = useToast();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [categoryError, setCategoryError] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [updateDesc, setUpdateDesc] = useState('');
  const [updateError, setUpdateError] = useState(false);

  const approve = useApproveInvoice(id);
  const cancel = useCancelInvoice(id);
  const del = useDeleteInvoice(id);
  const rollback = useRollbackInvoice(id);
  const addUpdate = useAddInvoiceUpdate(id);

  const status = detail.invoice_status;
  const canApprove = status === 'waiting-confirm';
  const canMarkPaid = status === 'approved';
  const canCancel = CANCELLABLE.includes(status);
  const canDelete = status === 'waiting-confirm';
  const canRollback = ROLLBACKABLE.includes(status);
  const canSendMail = detail.invoice_type !== 'deposit';

  const resetReason = () => {
    setCategory('');
    setMessage('');
    setCategoryError(false);
    setMessageError(false);
    setReason('');
    setReasonError(false);
    setUpdateDesc('');
    setUpdateError(false);
  };
  const closeDialog = () => {
    setDialog(null);
    resetReason();
  };

  const onError = (title: string) => (err: unknown) => {
    const m = err instanceof Error ? err.message : title;
    toast.error(title, { description: m });
  };

  const runApprove = (approvedAs: 'invoicable' | 'non-invoicable') => {
    approve.mutate(
      { status: approvedAs },
      {
        onSuccess: () => {
          toast.success(approvedAs === 'invoicable' ? 'Invoice approved' : 'Marked non-invoicable');
          setDialog(null);
        },
        onError: onError('Failed to approve invoice'),
      },
    );
  };

  /** Validate the shared category+reason fields; returns the trimmed pair or null. */
  const validCategoryReason = (): { category: string; message: string } | null => {
    const cat = category.trim();
    const msg = message.trim();
    setCategoryError(!cat);
    setMessageError(!msg);
    return cat && msg ? { category: cat, message: msg } : null;
  };

  const runCancel = () => {
    const v = validCategoryReason();
    if (!v) return;
    cancel.mutate(v, {
      onSuccess: () => { toast.success('Invoice cancelled'); closeDialog(); },
      onError: onError('Failed to cancel invoice'),
    });
  };

  const runDelete = () => {
    const v = validCategoryReason();
    if (!v) return;
    del.mutate(v, {
      onSuccess: () => { toast.success('Invoice deleted'); closeDialog(); },
      onError: onError('Failed to delete invoice'),
    });
  };

  const runRollback = () => {
    const msg = reason.trim();
    setReasonError(!msg);
    if (!msg) return;
    rollback.mutate(
      { message: msg },
      { onSuccess: () => { toast.success('Invoice rolled back'); closeDialog(); }, onError: onError('Failed to roll back invoice') },
    );
  };

  const runAddUpdate = () => {
    const desc = updateDesc.trim();
    setUpdateError(!desc);
    if (!desc) return;
    addUpdate.mutate(
      { invoice_update_description: desc },
      { onSuccess: () => { toast.success('Update added'); closeDialog(); }, onError: onError('Failed to add update') },
    );
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
        {canApprove ? (
          <Button size="sm" variant="primary" onClick={() => setDialog('approve')}>
            Approve
          </Button>
        ) : null}
        {canMarkPaid ? (
          <Button size="sm" variant="primary" onClick={() => setMarkPaidOpen(true)}>
            Mark paid
          </Button>
        ) : null}
        {canSendMail ? (
          <Button size="sm" variant="secondary" onClick={() => setMailOpen(true)}>
            Send mail
          </Button>
        ) : null}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton size="sm" variant="ghost" aria-label="More invoice actions">
              <MoreHorizontal size={15} />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canSendMail ? (
              <DropdownMenuItem onSelect={() => setPreviewOpen(true)}>Preview mail</DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={() => setDialog('addUpdate')}>Add update</DropdownMenuItem>
            {canRollback ? (
              <DropdownMenuItem onSelect={() => setDialog('rollback')}>Rollback</DropdownMenuItem>
            ) : null}
            {canCancel ? (
              <DropdownMenuItem tone="danger" onSelect={() => setDialog('cancel')}>
                Cancel invoice
              </DropdownMenuItem>
            ) : null}
            {canDelete ? (
              <DropdownMenuItem tone="danger" onSelect={() => setDialog('delete')}>
                Delete invoice
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Approve — invoicable vs non-invoicable via the same endpoint. */}
      <ConfirmDialog
        open={dialog === 'approve'}
        onOpenChange={(o) => (o ? setDialog('approve') : setDialog(null))}
        title="Approve invoice"
        description="Confirm this invoice as invoicable, or mark it non-invoicable to exclude it from billing."
        confirmLabel="Approve (invoicable)"
        loading={approve.isPending}
        onConfirm={() => runApprove('invoicable')}
      >
        <Button
          variant="secondary"
          onClick={() => runApprove('non-invoicable')}
          loading={approve.isPending}
          style={{ width: '100%' }}
        >
          Mark non-invoicable
        </Button>
      </ConfirmDialog>

      {/* Cancel — category + reason. */}
      <ConfirmDialog
        open={dialog === 'cancel'}
        onOpenChange={(o) => (o ? setDialog('cancel') : closeDialog())}
        title="Cancel invoice"
        description="Cancelling records a reason on the invoice's history."
        confirmLabel="Cancel invoice"
        destructive
        loading={cancel.isPending}
        onConfirm={runCancel}
      >
        <CategoryReasonFields
          category={category}
          message={message}
          categoryError={categoryError}
          messageError={messageError}
          onCategory={(v) => { setCategory(v); if (categoryError) setCategoryError(false); }}
          onMessage={(v) => { setMessage(v); if (messageError) setMessageError(false); }}
          reasonPlaceholder="Reason for cancelling this invoice"
        />
      </ConfirmDialog>

      {/* Delete — category + reason (waiting-confirm only). */}
      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={(o) => (o ? setDialog('delete') : closeDialog())}
        title="Delete invoice"
        description="Deleting removes the invoice. A category and reason are recorded."
        confirmLabel="Delete invoice"
        destructive
        loading={del.isPending}
        onConfirm={runDelete}
      >
        <CategoryReasonFields
          category={category}
          message={message}
          categoryError={categoryError}
          messageError={messageError}
          onCategory={(v) => { setCategory(v); if (categoryError) setCategoryError(false); }}
          onMessage={(v) => { setMessage(v); if (messageError) setMessageError(false); }}
          reasonPlaceholder="Reason for deleting this invoice"
        />
      </ConfirmDialog>

      {/* Rollback — reason only (paid/done → approved). */}
      <ConfirmDialog
        open={dialog === 'rollback'}
        onOpenChange={(o) => (o ? setDialog('rollback') : closeDialog())}
        title="Roll back invoice"
        description="Reverts a paid/done invoice to approved and refunds its transactions."
        confirmLabel="Roll back"
        destructive
        loading={rollback.isPending}
        onConfirm={runRollback}
      >
        <Field label="Reason" error={reasonError ? 'A reason is required.' : undefined}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(false); }}
              placeholder="Reason for rolling back this invoice"
            />
          )}
        </Field>
      </ConfirmDialog>

      {/* Add update — a manual log entry. */}
      <ConfirmDialog
        open={dialog === 'addUpdate'}
        onOpenChange={(o) => (o ? setDialog('addUpdate') : closeDialog())}
        title="Add invoice update"
        description="Append a manual note to the invoice's update history."
        confirmLabel="Add update"
        loading={addUpdate.isPending}
        onConfirm={runAddUpdate}
      >
        <Field label="Description" error={updateError ? 'A description is required.' : undefined}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={updateDesc}
              onChange={(e) => { setUpdateDesc(e.target.value); if (updateError) setUpdateError(false); }}
              placeholder="What changed on this invoice"
            />
          )}
        </Field>
      </ConfirmDialog>

      <InvoiceSendMailDrawer id={id} open={mailOpen} onOpenChange={setMailOpen} />
      <InvoicePreviewMailDialog id={id} open={previewOpen} onOpenChange={setPreviewOpen} />
      <InvoiceMarkPaidDrawer id={id} detail={detail} open={markPaidOpen} onOpenChange={setMarkPaidOpen} />
    </>
  );
}

/** Shared category-select + reason-textarea block for the cancel/delete dialogs. */
function CategoryReasonFields({
  category,
  message,
  categoryError,
  messageError,
  onCategory,
  onMessage,
  reasonPlaceholder,
}: {
  category: string;
  message: string;
  categoryError: boolean;
  messageError: boolean;
  onCategory: (v: string) => void;
  onMessage: (v: string) => void;
  reasonPlaceholder: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <Field label="Category" error={categoryError ? 'Select a category.' : undefined}>
        {() => (
          <Select value={category} onValueChange={onCategory}>
            <SelectTrigger invalid={categoryError}>
              <SelectValue placeholder="Select a reason category" />
            </SelectTrigger>
            <SelectContent>
              {REASON_CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label="Reason" error={messageError ? 'A reason is required.' : undefined}>
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={message}
            onChange={(e) => onMessage(e.target.value)}
            placeholder={reasonPlaceholder}
          />
        )}
      </Field>
    </div>
  );
}
